'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, ShoppingBag } from 'lucide-react';
import { mockApi } from '@/lib/mockApi';
import { api } from '@/lib/api';
import { getCategoryLabelEN } from '@/lib/categoryLabel';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/product/ProductCard';
import { BannerCarousel } from '@/components/marketing/BannerCarousel';
import type { Category, Product } from '@/types';

// Category icons mapping (using ShoppingBag as default for all)

export function HomePage() {
  const [featuredCategories, setFeaturedCategories] = useState<Category[]>([]);
  const [newestProducts, setNewestProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [homepageConfig, productsResponse, categoriesResponse] = await Promise.all([
          api.homepage.getPublic(),
          api.products.getProducts({ limit: 100 }), // Get up to 100 for homepage
          api.categories.getCategories(),
        ]);
        const allCategories = categoriesResponse.items;
        const products = productsResponse.items;

        const config = homepageConfig.config;
        
        // Get featured categories by IDs
        const featuredCategoryIds = config.featuredCategoryIds || [];
        const featuredCats = allCategories
          .filter(c => featuredCategoryIds.includes(c.id))
          .sort((a, b) => a.sort - b.sort);
        
        // If no featured categories in config, fallback to root categories
        if (featuredCats.length === 0) {
          const rootCategories = allCategories.filter(c => !c.parentId).sort((a, b) => a.sort - b.sort);
          setFeaturedCategories(rootCategories);
        } else {
          setFeaturedCategories(featuredCats);
        }
        
        // Get featured products by IDs (optional)
        const featuredProductIds = config.featuredProductIds || [];
        if (featuredProductIds.length > 0) {
          const featuredProds = products.filter(p => featuredProductIds.includes(p.id));
          setNewestProducts(featuredProds.length > 0 ? featuredProds : products.slice(0, 8));
        } else {
          setNewestProducts(products.slice(0, 8));
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);


  if (loading) {
    return (
      <PageContainer>
        <div className="animate-pulse space-y-12 py-12">
          {/* Hero skeleton */}
          <div className="h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl" />
          
          {/* Categories skeleton */}
          <div>
            <div className="h-8 w-48 bg-gray-200 rounded mb-6" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded-xl" />
              ))}
            </div>
          </div>
          
          {/* Products skeleton */}
          <div>
            <div className="h-8 w-48 bg-gray-200 rounded mb-6" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="space-y-3">
                  <div className="aspect-[4/5] bg-gray-200 rounded-lg" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-5 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Banner Carousel */}
      <BannerCarousel />

      {/* Featured Categories */}
      {featuredCategories.length > 0 && (
        <section className="py-10">
          <PageContainer>
            <h2 className="text-3xl font-bold mb-8 text-gray-900">Ангилалаар худалдаа</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {featuredCategories.map((category) => (
                <Link key={category.id} href={`/category/${category.id}`}>
                  <div className="group relative bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg hover:border-blue-300 transition-all duration-300 cursor-pointer">
                    <div className="flex justify-center mb-3 text-blue-600 group-hover:scale-110 transition-transform">
                      <ShoppingBag className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {getCategoryLabelEN(category.name)}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </PageContainer>
        </section>
      )}

      {/* Newest Products */}
      <section id="newest-products" className="py-10">
        <PageContainer>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Шинэ бараа</h2>
            <Link href="/products">
              <Button variant="outline" className="rounded-full">
                Бүгдийг харах
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {newestProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </PageContainer>
      </section>
    </div>
  );
}

